import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';

import {
  selectErrorEvents,
  selectMessages,
} from '../state/aiSidekickChatSlice';
import type { TransportRequestContext } from '../state/chatTypes';
import type { RetrySubmissionContext } from './useConversation.types';

/** Params accepted by {@link useConversationRetry}. */
export type UseConversationRetryParams = {
  /** Sends a question; returns `false` when it could not start. */
  sendMessage: (
    content: string,
    requestContext?: TransportRequestContext,
    retryContext?: RetrySubmissionContext
  ) => boolean;
  /** Claims the error for a retry; returns `false` when one already runs. */
  reserveRetry: (errorId: string) => boolean;
  /** Gives the claim back when the retry could not start. */
  cancelRetryReservation: (errorId: string) => void;
  /** Marks the claimed retry as running. */
  activateRetry: (errorId: string) => void;
};

/**
 * Retries the question behind an error card by resending its original
 * message with the request context it was first sent with.
 * @param params - The send action and the retry claim controls.
 * @returns A stable callback taking the error id.
 */
export const useConversationRetry = ({
  sendMessage,
  reserveRetry,
  cancelRetryReservation,
  activateRetry,
}: UseConversationRetryParams): ((errorId: string) => void) => {
  const errorEvents = useSelector(selectErrorEvents);
  const messages = useSelector(selectMessages);
  const errorEventsRef = useRef(errorEvents);
  errorEventsRef.current = errorEvents;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  return useCallback(
    (errorId: string): void => {
      const event = errorEventsRef.current.find((e) => e.id === errorId);
      if (event === undefined || !event.canRetry) {
        return;
      }
      const sourceMessage = messagesRef.current.find(
        (m) => m.id === event.sourceUserMessageId
      );
      if (sourceMessage === undefined) {
        return;
      }
      if (!reserveRetry(errorId)) {
        return;
      }
      if (
        !sendMessage(sourceMessage.content, event.requestContext, {
          errorId,
          sourceMessageId: sourceMessage.id,
        })
      ) {
        cancelRetryReservation(errorId);
        return;
      }
      activateRetry(errorId);
    },
    [activateRetry, cancelRetryReservation, reserveRetry, sendMessage]
  );
};
